import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import {
  selectIsAuthenticated,
  selectAuthLoading,
  selectUser,
  selectUserRoleId,
} from "../../store/selectors/authSelectors";
import { ROUTES } from "../../constants";
import { UserRole } from "../../constants";
import { getPostLoginRoute } from "../../utils/routeUtils";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * When provided, only the listed roles can access this route.
   * All other authenticated users are redirected to their home page.
   */
  allowedRoles?: number[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
}) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectAuthLoading);
  const user = useSelector(selectUser);
  const userRoleId = useSelector(selectUserRoleId);
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        Loading...
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // User is authenticated but profile data is still being fetched asynchronously
  // (initializeAuth → middleware → getCurrentUser). Block rendering to prevent
  // a flash of the wrong page before the role-based redirect fires.
  if (!user) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      />
    );
  }

  if (userRoleId !== null) {
    // Superadmin can only access routes that explicitly list their role.
    // Routes without allowedRoles are regular clinic-staff routes.
    if (
      userRoleId === UserRole.SuperAdmin &&
      !allowedRoles?.includes(UserRole.SuperAdmin)
    ) {
      return <Navigate to={ROUTES.TENANT_MANAGEMENT} replace />;
    }

    // Other roles: enforce allow-list if the route declares one
    if (allowedRoles && !allowedRoles.includes(userRoleId)) {
      return <Navigate to={getPostLoginRoute(userRoleId)} replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
